import { useEffect, useRef } from 'react'
import { useToast } from '../components/Toast/ToastContext'
import { getErrorMessage } from '../api/client'

/**
 * Shows an error toast when a TanStack Query hook enters an error state.
 *
 * A query that keeps failing the same way across refetches is one failure:
 * it toasts once, and stays quiet if that toast is dismissed, until the query
 * succeeds or fails differently. User-initiated toasts (saves) are separate
 * and always show.
 *
 * Usage:
 *   const { data, isError, error } = useSomeQuery(...)
 *   useQueryErrorToast(isError, error, 'loading records')
 *
 * The title says what didn't load (`loading records` reads "Couldn't load
 * records"); the message is the server's sentence, never a library's.
 */
export function useQueryErrorToast(isError: boolean, error: Error | null, context: string) {
  const { addToast } = useToast()
  const reportedFailureRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isError || !error) {
      reportedFailureRef.current = null
      return
    }
    const { code, status } = error as Error & { code?: string; status?: number }
    const sharedServerFailure = code === 'network_error' || (status !== undefined && status >= 500)
    // Failures are told apart by what actually went wrong; people see a
    // sentence about it.
    const message = getErrorMessage(error)
    // The same server failure can affect schema, views, and records at once.
    // Keep unrelated domain/validation failures scoped to their operation.
    const dedupeKey = JSON.stringify([
      'query-error',
      sharedServerFailure ? 'server' : context,
      code,
      status,
      error.message,
    ])
    if (reportedFailureRef.current === dedupeKey) return
    reportedFailureRef.current = dedupeKey
    addToast({
      type: 'error',
      title: sharedServerFailure
        ? "Couldn't load data"
        : `Couldn't ${context.replace(/^loading /, 'load ')}`,
      message,
      dedupeKey,
    })
  }, [isError, error, context, addToast])
}
